import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../api/client";
import StatusBadge from "../shared/StatusBadge";
import "./BillingPage.css";

const formatCAD = (n) =>
    new Intl.NumberFormat("en-CA", {
        style: "currency",
        currency: "CAD",
    }).format(n ?? 0);

const QUOTE_STATUSES = ["", "draft", "sent", "accepted", "declined"];
const INVOICE_STATUSES = ["", "draft", "sent", "paid", "overdue"];

const BillingPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    // Determine initial tab from ?tab= query param
    const searchParams = new URLSearchParams(location.search);
    const [tab, setTab] = useState(
        searchParams.get("tab") === "invoices" ? "invoices" : "quotes",
    );

    // Quotes state
    const [quotes, setQuotes] = useState([]);
    const [quotesLoading, setQuotesLoading] = useState(false);
    const [quoteSearch, setQuoteSearch] = useState("");
    const [quoteSearchInput, setQuoteSearchInput] = useState("");
    const [quoteStatus, setQuoteStatus] = useState("");

    // Invoices state
    const [invoices, setInvoices] = useState([]);
    const [invoicesLoading, setInvoicesLoading] = useState(false);
    const [invoiceStatus, setInvoiceStatus] = useState("");

    const loadQuotes = useCallback(() => {
        setQuotesLoading(true);
        api.quotes({
            status: quoteStatus || undefined,
            search: quoteSearch || undefined,
        })
            .then((r) => setQuotes(r.quotes?.data || r.quotes || []))
            .finally(() => setQuotesLoading(false));
    }, [quoteStatus, quoteSearch]);

    const loadInvoices = useCallback(() => {
        setInvoicesLoading(true);
        api.invoices({ status: invoiceStatus || undefined })
            .then((r) => setInvoices(r.invoices?.data || r.invoices || []))
            .finally(() => setInvoicesLoading(false));
    }, [invoiceStatus]);

    useEffect(() => {
        if (tab === "quotes") loadQuotes();
    }, [tab, loadQuotes]);

    useEffect(() => {
        if (tab === "invoices") loadInvoices();
    }, [tab, loadInvoices]);

    // Debounce quote search
    useEffect(() => {
        const timer = setTimeout(() => setQuoteSearch(quoteSearchInput), 350);
        return () => clearTimeout(timer);
    }, [quoteSearchInput]);

    const switchTab = (t) => {
        setTab(t);
        navigate(`/admin/billing?tab=${t}`, { replace: true });
    };

    return (
        <div className="billing-page">
            <div className="billing-page__header">
                <h2>Billing</h2>
                <Link
                    to={
                        tab === "quotes"
                            ? "/admin/quotes/new"
                            : "/admin/invoices/new"
                    }
                    className="btn btn--primary btn--sm"
                >
                    {tab === "quotes" ? t("quote.new") : t("invoice.new")}
                </Link>
            </div>

            {/* Tabs */}
            <div className="billing-page__tabs">
                <button
                    className={`billing-page__tab ${tab === "quotes" ? "billing-page__tab--active" : ""}`}
                    onClick={() => switchTab("quotes")}
                >
                    Quotes
                </button>
                <button
                    className={`billing-page__tab ${tab === "invoices" ? "billing-page__tab--active" : ""}`}
                    onClick={() => switchTab("invoices")}
                >
                    Invoices
                </button>
            </div>

            {/* ── Quotes ──────────────────────────────────────────────── */}
            {tab === "quotes" && (
                <div>
                    <div className="billing-page__toolbar">
                        <div className="billing-page__filters">
                            <select
                                value={quoteStatus}
                                onChange={(e) => setQuoteStatus(e.target.value)}
                                className="billing-page__select"
                            >
                                <option value="">All statuses</option>
                                {QUOTE_STATUSES.filter(Boolean).map((s) => (
                                    <option key={s} value={s}>
                                        {s.charAt(0).toUpperCase() + s.slice(1)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="billing-page__search">
                            <input
                                type="search"
                                placeholder="Search by title…"
                                value={quoteSearchInput}
                                onChange={(e) =>
                                    setQuoteSearchInput(e.target.value)
                                }
                            />
                        </div>
                    </div>
                    <div
                        className="card"
                        style={{ padding: 0, overflow: "hidden" }}
                    >
                        <table className="dash-table">
                            <thead>
                                <tr>
                                    <th>{t("quote.quoteTitle")}</th>
                                    <th>{t("quote.client")}</th>
                                    <th>{t("quote.status")}</th>
                                    <th>Expires</th>
                                    <th className="dash-table__right">
                                        {t("quote.total")}
                                    </th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {quotesLoading && (
                                    <tr>
                                        <td
                                            colSpan={6}
                                            className="dash-table__empty"
                                        >
                                            {t("common.loading")}
                                        </td>
                                    </tr>
                                )}
                                {!quotesLoading && quotes.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={6}
                                            className="dash-table__empty"
                                        >
                                            {t("quote.noQuotes")}
                                        </td>
                                    </tr>
                                )}
                                {quotes.map((q) => (
                                    <tr key={q.id}>
                                        <td>
                                            <Link
                                                to={`/admin/quotes/${q.id}`}
                                                className="billing-page__link"
                                            >
                                                {q.title}
                                            </Link>
                                        </td>
                                        <td className="dash-table__muted">
                                            {q.client ? (
                                                <button
                                                    className="billing-page__client-btn"
                                                    onClick={() =>
                                                        navigate(
                                                            `/admin/clients/${q.client_id}`,
                                                        )
                                                    }
                                                >
                                                    {q.client.name}
                                                </button>
                                            ) : (
                                                "—"
                                            )}
                                        </td>
                                        <td>
                                            <StatusBadge status={q.status} />
                                        </td>
                                        <td className="dash-table__muted">
                                            {q.expires_at
                                                ? new Date(
                                                      q.expires_at,
                                                  ).toLocaleDateString("en-CA")
                                                : "—"}
                                        </td>
                                        <td className="dash-table__right dash-table__mono">
                                            {formatCAD(q.total)}
                                        </td>
                                        <td>
                                            <Link
                                                to={`/admin/quotes/${q.id}`}
                                                className="btn btn--ghost btn--xs"
                                            >
                                                {t("common.edit")}
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ── Invoices ────────────────────────────────────────────── */}
            {tab === "invoices" && (
                <div>
                    <div className="billing-page__toolbar">
                        <div className="billing-page__filters">
                            <select
                                value={invoiceStatus}
                                onChange={(e) =>
                                    setInvoiceStatus(e.target.value)
                                }
                                className="billing-page__select"
                            >
                                <option value="">All statuses</option>
                                {INVOICE_STATUSES.filter(Boolean).map((s) => (
                                    <option key={s} value={s}>
                                        {s.charAt(0).toUpperCase() + s.slice(1)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div
                        className="card"
                        style={{ padding: 0, overflow: "hidden" }}
                    >
                        <table className="dash-table">
                            <thead>
                                <tr>
                                    <th>{t("invoice.number")}</th>
                                    <th>{t("invoice.client")}</th>
                                    <th>{t("invoice.dueDate")}</th>
                                    <th>{t("invoice.status")}</th>
                                    <th className="dash-table__right">
                                        {t("invoice.total")}
                                    </th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoicesLoading && (
                                    <tr>
                                        <td
                                            colSpan={6}
                                            className="dash-table__empty"
                                        >
                                            {t("common.loading")}
                                        </td>
                                    </tr>
                                )}
                                {!invoicesLoading && invoices.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={6}
                                            className="dash-table__empty"
                                        >
                                            {t("invoice.noInvoices")}
                                        </td>
                                    </tr>
                                )}
                                {invoices.map((inv) => (
                                    <tr key={inv.id}>
                                        <td className="dash-table__mono">
                                            <Link
                                                to={`/admin/invoices/${inv.id}`}
                                                className="billing-page__link"
                                            >
                                                {inv.invoice_number}
                                            </Link>
                                        </td>
                                        <td className="dash-table__muted">
                                            {inv.client ? (
                                                <button
                                                    className="billing-page__client-btn"
                                                    onClick={() =>
                                                        navigate(
                                                            `/admin/clients/${inv.client_id}`,
                                                        )
                                                    }
                                                >
                                                    {inv.client.name}
                                                </button>
                                            ) : (
                                                "—"
                                            )}
                                        </td>
                                        <td className="dash-table__muted">
                                            {inv.due_date
                                                ? new Date(
                                                      inv.due_date,
                                                  ).toLocaleDateString("en-CA")
                                                : "—"}
                                        </td>
                                        <td>
                                            <StatusBadge status={inv.status} />
                                        </td>
                                        <td className="dash-table__right dash-table__mono">
                                            {formatCAD(inv.total)}
                                        </td>
                                        <td>
                                            <Link
                                                to={`/admin/invoices/${inv.id}`}
                                                className="btn btn--ghost btn--xs"
                                            >
                                                {t("common.edit")}
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BillingPage;
